import {
  Briefcase,
  ChartColumn,
  PackageCheck,
  Package,
  ShieldCheck,
  Truck,
  TriangleAlert,
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { DashboardRole } from '@/types/session';
import { messages } from '@/i18n';

export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

export interface RoleDefinition {
  /** Where this role lands immediately after /me resolves. */
  landing: string;
  /** The role's entire navigation. A role sees nothing outside its own list. */
  nav: readonly NavItem[];
}

const nav = messages.nav;

/**
 * The role → landing route and navigation map from § 3.
 *
 * Every route below renders a placeholder in this slice. The point of building
 * it now is that the guard chain, this map and the shell are proven before six
 * teams' worth of screens are built on top of them.
 */
export const ROLE_ROUTES: Record<DashboardRole, RoleDefinition> = {
  Tollit: {
    landing: '/control-tower',
    nav: [
      { to: '/control-tower', label: nav.controlTower, icon: Briefcase },
      { to: '/control-tower/shipments', label: nav.activeShipments, icon: Package },
      { to: '/control-tower/delivered', label: nav.delivered, icon: PackageCheck },
      { to: '/control-tower/deviations', label: nav.deviations, icon: TriangleAlert },
      { to: '/control-tower/collections/ready', label: nav.readyForCollection, icon: Truck },
      { to: '/control-tower/collections/collected', label: nav.collected, icon: PackageCheck },
      { to: '/control-tower/reports/booking', label: nav.bookingReport, icon: ChartColumn },
      { to: '/control-tower/access', label: nav.access, icon: ShieldCheck },
    ],
  },
  Driver: {
    landing: '/driver/collections/ready',
    nav: [
      { to: '/driver/collections/ready', label: nav.readyForCollection, icon: Truck },
      { to: '/driver/collections/collected', label: nav.collected, icon: PackageCheck },
    ],
  },
  GroundHandler: {
    landing: '/handler/shipments',
    nav: [{ to: '/handler/shipments', label: nav.shipments, icon: Package }],
  },
  ReceivingTerminal: {
    landing: '/terminal/shipments',
    nav: [{ to: '/terminal/shipments', label: nav.shipments, icon: Package }],
  },
  LastMileCustomerService: {
    landing: '/last-mile/shipments',
    nav: [{ to: '/last-mile/shipments', label: nav.shipments, icon: Package }],
  },
  SenderCustomer: {
    landing: '/shipments',
    nav: [{ to: '/shipments', label: nav.shipments, icon: Package }],
  },
};

export function landingRouteFor(role: DashboardRole): string {
  return ROLE_ROUTES[role].landing;
}

export function navItemsFor(role: DashboardRole): readonly NavItem[] {
  return ROLE_ROUTES[role].nav;
}
